import { List, Map, fromJS } from 'immutable'
import _replace from 'lodash/replace'
import _toNumber from 'lodash/toNumber'
import { TYPE_MEMBER_HOME_FIRST_TOUCH } from 'services/pm-section'
import { getFormattedContentData } from 'services/content'
import { TYPE_CONTENT_SERIES } from 'services/content-type'
import {
  CLICK_TO_DETAILS_PAGE_GA4_EVENT,
  PLAY_CLICK_GA4_EVENT,
  UI_ELEMENT,
  UI_LOCATION,
} from 'services/event-tracking'

// Narrow-layout breakpoint (overlay + slide hook).
export const MOBILE_MAX_PX = 768

// Scroll position (px): shell treats member home as “at top” for dismiss logic.
export const SCROLL_SHOW_TOP_PX = 16

// Scroll delta (px) past top that contributes to dismiss.
export const SCROLL_DISMISS_PX = 72

/*
 * Swipe dismiss/restore: fraction of panel height (0–1) where releasing commits.
 * Consumed by useMemberHomeFirstTouchSlide.
 */
export const SWIPE_COMMIT_RATIO = 0.22

/*
 * Touch drag scaling: multiplier grows as more of the panel is off-screen (quadratic).
 * Consumed by useMemberHomeFirstTouchSlide.
 */
export const DRAG_EXPONENT = 2

// Couples with DRAG_EXPONENT for drag multiplier curve.
export const DRAG_EXP_COEFF = 3.5

// Programmatic slide-off animation duration (ms).
export const DISMISS_TRANSITION_MS = 420

// Easing when animating dismiss (ease-in, accelerates off-screen).
export const DISMISS_TRANSITION_EASING = 'cubic-bezier(0.42, 0, 1, 1)'

// Snap-back / reveal duration when restoring the panel (ms).
export const SNAP_BACK_TRANSITION_MS = 380

// Easing for snap-back (ease-out).
export const SNAP_BACK_TRANSITION_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)'

// Pull-strip hit area height when overlay is mostly dismissed (px).
export const PULL_STRIP_HEIGHT_PX = 72

// Form field name for the AI prompt (MemberHomeFirstTouchAiInput).
export const AI_PROMPT_FIELD = 'memberHomeFirstTouchAiPrompt'

/*
 * Static-text keys for seed pills under the AI field (MemberHomeFirstTouchAiInput).
 * Order is UI order, not alphabetical.
 */
export const MEMBER_HOME_FIRST_TOUCH_AI_INPUT_SEED_PROMPT_KEYS = [
  'calming',
  'mysterious',
  'profound',
]

/*
 * Static-text keys for mood pills (MemberHomeFirstTouchAiPrompts, centered).
 * Order is UI order, not alphabetical.
 */
export const MEMBER_HOME_FIRST_TOUCH_MOOD_PROMPT_KEYS = [
  'relieved',
  'energized',
  'calm',
  'dramatic',
  'composed',
]

// Fallback PM displayMode when missing (BEM + branching).
export const MEMBER_HOME_FIRST_TOUCH_VARIATION_DEFAULT = 'generic'

/*
 * PM displayMode slugs (GraphQL / Redux data.displayMode).
 * Drive member-home-first-touch--{slug} and background modifiers.
 */
export const MEMBER_HOME_FIRST_TOUCH_VARIATIONS = {
  AI_FOCUSED: 'ai-focused',
  AI_PLUS_GENERIC: 'ai-plus-generic',
  AI_VOICE_FOCUS: 'ai-voice-focus',
  COURSE: 'course',
  ENGAGEMENT: 'engagement',
  GENERIC: 'generic',
  SERIES: 'series',
}

// Maps PM tab row tabType slug → staticText key (memberHomeFirstTouch bundle).
const SLUG_STATIC_KEYS = {
  'continue-watching': 'tabContinueWatching',
  'new-for-you': 'tabNewForYou',
  'next-step': 'nextStep',
  'recommended-for-you': 'tabNewForYou',
  'todays-practice': 'todaysPractice',
  'watch-next-episode': 'watchNextEpisode',
}

/*
 * Light vs dark chrome (header + BEM --light / --dark).
 * @param {string | null | undefined} variation PM displayMode slug
 * @returns {'light' | 'dark'}
 */
export function getMemberHomeFirstTouchColorScheme (variation) {
  if (variation === MEMBER_HOME_FIRST_TOUCH_VARIATIONS.COURSE
    || variation === MEMBER_HOME_FIRST_TOUCH_VARIATIONS.ENGAGEMENT) {
    return 'light'
  }
  return 'dark'
}

/*
 * Background layer BEM class for the current variation.
 * @param {string | null | undefined} variation
 * @returns {string} Class on member-home-first-touch__background
 */
export function getBgClassName (variation) {
  switch (variation) {
    case MEMBER_HOME_FIRST_TOUCH_VARIATIONS.COURSE:
      return 'member-home-first-touch__background--course-light'
    case MEMBER_HOME_FIRST_TOUCH_VARIATIONS.SERIES:
      return 'member-home-first-touch__background--series-dark'
    case MEMBER_HOME_FIRST_TOUCH_VARIATIONS.AI_VOICE_FOCUS:
      return 'member-home-first-touch__background--ai-voice-focus-dark'
    case MEMBER_HOME_FIRST_TOUCH_VARIATIONS.AI_PLUS_GENERIC:
      return 'member-home-first-touch__background--ai-plus-generic-dark'
    case MEMBER_HOME_FIRST_TOUCH_VARIATIONS.ENGAGEMENT:
      return 'member-home-first-touch__background--engagement-light'
    default:
      return 'member-home-first-touch__background--generic-dark'
  }
}

/*
 * Root overlay classes: variation slug, light/dark, optional dismissed.
 * @param {{ dismissed: boolean, variation?: unknown }} params
 * @returns {string}
 */
export function memberHomeFirstTouchRootClassName ({ dismissed, variation }) {
  const variationSlug = variation || MEMBER_HOME_FIRST_TOUCH_VARIATION_DEFAULT
  const backgroundPrefix = getMemberHomeFirstTouchColorScheme(variation)
  return [
    'member-home-first-touch',
    `member-home-first-touch--${variationSlug}`,
    `member-home-first-touch--${backgroundPrefix}`,
    dismissed && 'member-home-first-touch--dismissed',
  ].filter(Boolean).join(' ')
}

/*
 * Extract data + sectionId from first-touch row payload.
 * Supports Redux “data only” shape or wrapper Map with id + nested data.
 * @param {import('immutable').Map | null | undefined} sectionPayload
 * @returns {{ data: import('immutable').Map, sectionId: unknown }}
 */
export function getMemberHomeFirstTouchContent (sectionPayload) {
  const root = Map.isMap(sectionPayload) ? sectionPayload : Map()
  const data = root.has('data') ? root.get('data', Map()) : root
  const sectionId = root.get('id')
  return { data, sectionId }
}

/*
 * First-touch row `data` from state.pmSection (list of PM sections).
 * @param {ReduxState} state
 * @returns {import('immutable').Map}
 */
export function selectMemberHomeFirstTouchSectionData (state) {
  const pmSection = Map.isMap(state.pmSection) ? state.pmSection : Map()
  const section = pmSection.find((s) => (
    Map.isMap(s) && s.getIn(['data', 'type']) === TYPE_MEMBER_HOME_FIRST_TOUCH
  ))
  if (!section || !Map.isMap(section)) {
    return Map()
  }
  return section.get('data') || Map()
}

/*
 * Tab rows for tiles: data.tabs as Immutable List or plain array (e.g. SSR).
 * @param {import('immutable').Map | object | null | undefined} memberHomeFirstTouchData
 * @returns {import('immutable').Map[]}
 */
export function getFirstTouchTileContentFromPmData (memberHomeFirstTouchData) {
  if (memberHomeFirstTouchData == null) {
    return []
  }
  const tabs = Map.isMap(memberHomeFirstTouchData)
    ? memberHomeFirstTouchData.get('tabs')
    : memberHomeFirstTouchData.tabs
  if (tabs == null) {
    return []
  }
  if (List.isList(tabs)) {
    return tabs.toArray()
  }
  if (Array.isArray(tabs)) {
    return tabs
  }
  return []
}

/*
 * Tab row label from tabType slug + memberHomeFirstTouch staticText Map.
 * @param {string} tabType
 * @param {import('immutable').Map} staticText
 * @returns {string}
 */
export function getTabLabel (tabType, staticText) {
  const key = SLUG_STATIC_KEYS[tabType]
  if (key) {
    const translated = staticText.get(key)
    if (translated) {
      return translated
    }
  }
  return ''
}

/**
 * Formatted content Map for first-touch tiles / GA (single getFormattedContentData pass).
 * @param {object} rawContent Plain or Immutable-serializable PM content node
 * @returns {import('immutable').Map | null}
 */
export function getMemberHomeFirstTouchFormattedContent (rawContent) {
  if (rawContent == null || typeof rawContent !== 'object') {
    return null
  }
  const formatted = getFormattedContentData(fromJS(rawContent))
  if (!Map.isMap(formatted) || formatted.isEmpty()) {
    return null
  }
  return formatted
}

/*
 * Build GA4 payload for a Member Home First Touch row label click.
 * @param {{
 *   formatted?: import('immutable').Map | null,
 *   rawContent?: object,
 *   rowPlacement: number,
 *   rowName: string,
 *   recommId: unknown,
 * }} params
 * @returns {{ href: string, gaBase: Record<string, unknown> } | null}
 */
export function buildMemberHomeFirstTouchContentGaPayload ({
  formatted: formattedInput,
  rawContent,
  rowPlacement,
  rowName,
  recommId,
}) {
  const formatted = (Map.isMap(formattedInput) && !formattedInput.isEmpty())
    ? formattedInput
    : getMemberHomeFirstTouchFormattedContent(rawContent)
  if (!formatted) {
    return null
  }
  const href = formatted.get('path') || ''
  if (!href) {
    return null
  }
  const contentType = formatted.get('contentType') || ''
  const id = formatted.get('contentId') || formatted.get('id')
  const isSeries = contentType === TYPE_CONTENT_SERIES
  const mediaId = _toNumber(
    formatted.getIn(['featuredEpisode', 'data', 'mediaId'])
    || formatted.getIn(['feature', 'id'])
    || formatted.getIn(['featureMedia', 'mediaId'])
    || 0,
  )
  const parentSeriesId = isSeries ? id : formatted.get('seriesId')
  const parentSeriesData = parentSeriesId ? {
    parent_series_name: isSeries ? formatted.get('title') : formatted.get('seriesTitle'),
    parent_series_id: parentSeriesId,
  } : {}
  const rowData = {
    row_placement: rowPlacement,
    tile_number: 0,
    row_name: rowName,
    ...(recommId != null && recommId !== '' ? { recomm_id: recommId } : {}),
  }
  const gaBase = {
    content_id: id,
    content_name: formatted.get('title'),
    content_type: contentType,
    media_language: formatted.get('mediaLanguage') || formatted.get('languageFullText'),
    media_id: mediaId,
    ui_location: UI_LOCATION.MEMBER_HOME,
    ui_element: UI_ELEMENT.FIRST_TOUCH,
    click_attribution: UI_LOCATION.MEMBER_HOME,
    ...parentSeriesData,
    ...rowData,
  }
  return { href, gaBase }
}

/**
 * Create a handler for a row label click
 * @param {{
 *   sendOptimizelyFeatureEvent: Function,
 *   setEventDefault: Function,
 *   rowGa: { href: string, gaBase: Record<string, unknown> } | null | undefined,
 * }} params
 * @returns {(() => void) | undefined}
 */
export function createMemberHomeFirstTouchRowLabelClickHandler ({
  sendOptimizelyFeatureEvent,
  setEventDefault,
  rowGa,
}) {
  if (!rowGa) {
    return undefined
  }
  return () => {
    setEventDefault(CLICK_TO_DETAILS_PAGE_GA4_EVENT.mergeDeep(rowGa.gaBase))
    sendOptimizelyFeatureEvent({ eventName: 'click_to_detail_page' })
  }
}

/**
 * Create a handler for a tile play click
 * @param {{
 *   sendOptimizelyFeatureEvent: Function,
 *   setEventDefault: Function,
 *   rowGa: { href: string, gaBase: Record<string, unknown> } | null | undefined,
 * }} params
 * @returns {(() => void) | undefined}
 */
export function createMemberHomeFirstTouchThumbPlayClickHandler ({
  sendOptimizelyFeatureEvent,
  setEventDefault,
  rowGa,
}) {
  if (!rowGa) {
    return undefined
  }
  return () => {
    setEventDefault(PLAY_CLICK_GA4_EVENT.mergeDeep({
      ...rowGa.gaBase,
      is_featured_media: true,
    }))
    sendOptimizelyFeatureEvent({
      eventName: 'play_click',
    })
  }
}

/*
 * Overlay header strings from variation + static text.
 * Replaces { $userFirstName } and { $contentTitle } when those placeholders exist.
 * @param {{
 *   variation: string | null | undefined,
 *   staticText: import('immutable').Map,
 *   userFirstName: string,
 *   contentTitle: string,
 * }} props
 * @returns {{ title: string, subtitle: string }}
 */
export function getHeaderStrings (props) {
  const {
    variation,
    staticText,
    userFirstName,
    contentTitle,
  } = props
  let title = ''
  let subtitle = ''

  switch (variation) {
    case MEMBER_HOME_FIRST_TOUCH_VARIATIONS.COURSE:
      title = staticText.get('titleGladToSeeYou')
      subtitle = staticText.get('subtitleHowsYourJourneyGoing')
      break
    case MEMBER_HOME_FIRST_TOUCH_VARIATIONS.SERIES:
      title = staticText.get('titleWelcomeBack')
      subtitle = staticText.get('subtitleBackTo')
      break
    case MEMBER_HOME_FIRST_TOUCH_VARIATIONS.AI_FOCUSED:
    case MEMBER_HOME_FIRST_TOUCH_VARIATIONS.AI_PLUS_GENERIC:
      title = staticText.get('titleGladToSeeYou')
      subtitle = staticText.get('subtitleLetsPickUpTheThread')
      break
    case MEMBER_HOME_FIRST_TOUCH_VARIATIONS.ENGAGEMENT:
      title = staticText.get('titleNiceToSeeYou')
      subtitle = staticText.get('subtitleHowDoYouWantToFeelToday')
      break
    case MEMBER_HOME_FIRST_TOUCH_VARIATIONS.AI_VOICE_FOCUS:
      title = staticText.get('titleGladToSeeYou')
      subtitle = staticText.get('subtitleWhatAreYouLookingFor')
      break
    default:
      title = staticText.get('titleWelcomeBack')
      subtitle = staticText.get('subtitleWhatAreYouLookingFor')
      break
  }

  title = _replace(title, '{ $userFirstName }', userFirstName)
  subtitle = _replace(subtitle, '{ $contentTitle }', contentTitle)
  return { title, subtitle }
}

/*
 * Touch delta multiplier from how far the panel is off-screen (0 = flush, 1 = fully hidden).
 * @param {number} fractionOffScreen
 * @returns {number}
 */
export function slideDragMultiplier (fractionOffScreen) {
  const t = Math.min(1, Math.max(0, fractionOffScreen))
  return 1 + DRAG_EXP_COEFF * (t ** DRAG_EXPONENT)
}

/*
 * Clamp vertical translate: keeps panel between maxSlidePx (negative) and 0.
 * @param {number} px
 * @param {number} maxSlidePx Upper bound (negative, e.g. -800)
 * @returns {number}
 */
export function clampSlidePx (px, maxSlidePx) {
  return Math.max(maxSlidePx, Math.min(0, px))
}
